/* ---------------------------------------------------------------------------
   How much of the DOM a state change requires rebuilding.

   src/main.ts replaces what it renders rather than diffing it, which is fine for
   a screen that changes when you navigate and wrong for a dialog you are typing
   into: every keystroke used to throw away the titlebar, the nav pane and a
   screenful of panels behind the dialog and build them again, which reads as the
   background flickering.

   So the render pass asks this first. Three answers: rebuild everything, rebuild
   only the dialog layer, or — for a keystroke that has already put itself on
   screen in the control it landed in — touch nothing at all.
   --------------------------------------------------------------------------- */

import type { State } from './store.ts';
import { editDialogNeedsPaint } from '../screens/editDialog.ts';
import { inspectionDialogNeedsPaint } from '../screens/inspectionDialog.ts';
import { defectDialogNeedsPaint } from '../screens/defectDialog.ts';
import { progressDialogNeedsPaint } from '../screens/progressDialog.ts';
import { siteDialogsNeedPaint } from '../screens/siteDialogs.ts';
import { qualityDialogsNeedPaint } from '../screens/siteQuality.ts';

/**
 * State no part of the shell is rendered from — only the dialogs read any of it.
 *
 * Verified rather than assumed: nothing under src/screens or src/components
 * mentions one of these outside the three dialog files themselves. A field added
 * here that the shell does read would leave the screen stale.
 */
export const DIALOG_KEYS: ReadonlySet<keyof State> = new Set([
  'editingId',
  'editCreating',
  'editDraft',
  'editSaving',
  'editError',
  'editFill',
  'editOther',
  'addingInspectionId',
  'inspectionDraft',
  'inspectionSaving',
  'inspectionError',
  /* "Log defect". `registerDefects` is deliberately NOT here: the overview and
     the record are both drawn from it, so a recorded defect has to rebuild the
     shell as well as close the dialog. */
  'defectBridgeId',
  'defectDraft',
  'defectSaving',
  'defectError',
  /* Folding the component picker is drawn by nothing but the dialog, so it costs
     the shell behind it no repaint — but it IS drawn, so it is not in the
     keystroke exemption below either. */
  'defectTreeOpen',
  /* The picker's find box. Unlike the note, typing into it DOES paint — the
     rows under it are the answer — and its id is what keeps focus across that. */
  'defectTreeFilter',
  /* "Record work". `progress` and `progressSummary` are deliberately NOT here:
     the record's Works card is drawn from both, so a recorded entry has to
     rebuild the shell as well as close the sheet — the same rule
     `registerDefects` keeps two entries above. */
  'progressBridgeId',
  'progressDraft',
  'progressSaving',
  'progressError',
  /* The daily return, the incident and the document. `siteReturns`,
     `incidents` and `documents` are deliberately NOT here for the same reason
     `progress` is not: the record's cards are drawn from all three, so a
     recorded return has to rebuild the shell as well as close the sheet. */
  'returnBridgeId',
  'returnDraft',
  'returnSaving',
  'returnError',
  'returnFrozen',
  'incidentBridgeId',
  'incidentDraft',
  'incidentSaving',
  'incidentError',
  'documentBridgeId',
  'documentDraft',
  'documentSaving',
  'documentError',
  /* A test result and a raised item (LC-53, LC-54). `tests` and `items` are
     not here, for the reason `documents` is not. */
  'testBridgeId',
  'testDraft',
  'testSaving',
  'testError',
  'itemBridgeId',
  'itemDraft',
  'itemSaving',
  'itemError',
  /* The bulk import. `importCsv` is here too: the textarea already shows what
     was typed, so a paste of ten thousand lines costs no render — but the plan
     is cleared by the same action, and importPlan IS drawn, so a repaint still
     happens when it needs to. */
  'importOpen',
  'importCsv',
  'importPlan',
  'importBusy',
  'importError',
  'photoDefectId',
  'photoDefectN',
  'inspectionPhotoId',
  'progressPhotoId',
  'worksEntryId',
  /* The delete confirmation's own three. `models` is deliberately NOT here: the
     record's panel is drawn from it, so the re-read that follows a delete has to
     rebuild the shell as well as the dialog. */
  'modelDeleteBridgeId',
  'modelDeleteBusy',
  'modelDeleteError',
  /* The import's summary, which only src/modeler/importDialog.ts reads. Its
     sibling `modelImport` is deliberately NOT here — the record's button and
     hero both show the import while it runs, and the live viewport is unmounted
     off the same field. */
  'modelImportResult',
  /* Edit state. `phaseBusy` and `phaseNote` are NOT here: the State page reads
     both, and an act re-reads the register the record is drawn from. */
  'stateEditOpen',
  'stateDraft',
  /* The handover dialog's open flag. `handover` itself is NOT here: the
     bridge dashboard's Handover tile is drawn from it. */
  'handoverOpen',
]);

export type Scope = 'shell' | 'dialogs' | 'nothing';

export function scopeFor(prev: Readonly<State> | null, next: Readonly<State>): Scope {
  if (!prev) return 'shell';

  const moved = (Object.keys(next) as (keyof State)[]).filter((k) => !Object.is(prev[k], next[k]));
  // Nothing moved at all: paint() was called by hand rather than by the store —
  // an image slot gained a photo, and those live outside state.
  if (!moved.length) return 'shell';
  if (!moved.every((k) => DIALOG_KEYS.has(k))) return 'shell';

  /* Only dialog state moved. If all of it was keystrokes echoing back into a
     draft, the control each one landed in already shows what was typed and
     nothing drawn from it has changed. Each dialog answers for its own fields —
     this module does not know what they derive, only who to ask. */
  const derived =
    moved.some(
      (k) =>
        k !== 'editDraft' &&
        k !== 'inspectionDraft' &&
        k !== 'defectDraft' &&
        k !== 'progressDraft' &&
        k !== 'returnDraft' &&
        k !== 'incidentDraft' &&
        k !== 'documentDraft' &&
        k !== 'testDraft' &&
        k !== 'itemDraft' &&
        k !== 'stateDraft',
    ) ||
    (moved.includes('editDraft') && editDialogNeedsPaint(next)) ||
    (moved.includes('inspectionDraft') &&
      inspectionDialogNeedsPaint(prev.inspectionDraft, next.inspectionDraft)) ||
    (moved.includes('defectDraft') && defectDialogNeedsPaint(prev.defectDraft, next.defectDraft)) ||
    (moved.includes('progressDraft') &&
      progressDialogNeedsPaint(prev.progressDraft, next.progressDraft)) ||
    ((moved.includes('returnDraft') ||
      moved.includes('incidentDraft') ||
      moved.includes('documentDraft')) &&
      siteDialogsNeedPaint(prev, next)) ||
    ((moved.includes('testDraft') || moved.includes('itemDraft')) && qualityDialogsNeedPaint(prev, next));

  return derived ? 'dialogs' : 'nothing';
}
